import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Descriptions, Empty, Modal, Spin, Tooltip } from 'antd';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import phytosanitaryProductApi from '../../apis/PhytosanitaryProductApi';
import { PhytosanitaryProduct } from '../../models/Entities';
import styles from './PhytosanitaryProductComponent.module.scss';
import alertService from '../../services/AlertService';

/**
 * Renders an info button that, when clicked, retrieves a phytosanitary product by its register number
 * and shows its information in a modal.
 * @param props the register number of the product (it can be undefined).
 * @returns the phytosanitary product component.
 */
const PhytosanitaryProductComponent = (props: PhytosanitaryProductProps) => {
    const { registerNumber } = props;
    /*** HOOKS ***/

    const intl = useIntl();
    const [product, setProduct] = useState<PhytosanitaryProduct>();
    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    /*** METHODS ***/

    const showModal = async () => {
        try {
            setOpen(true);
            if (registerNumber) {
                setLoading(true);
                const page = await phytosanitaryProductApi.list(0, 1, 'name', true, registerNumber);
                setProduct(page.content[0]);
            }
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    /*** VISUAL ***/

    const items: { label: string; value?: string }[] = product
        ? [
              { label: 'phytosanitaryProduct.registerNumber', value: product.registerNumber },
              { label: 'phytosanitaryProduct.holder', value: product.holder },
              { label: 'phytosanitaryProduct.manufacturer', value: product.manufacturer },
              { label: 'phytosanitaryProduct.formulation', value: product.formulation },
              { label: 'phytosanitaryProduct.status', value: product.status },
              {
                  label: 'phytosanitaryProduct.domestic',
                  value:
                      product.domestic === 'True'
                          ? intl.formatMessage({ id: 'common.yes' })
                          : product.domestic === 'False'
                          ? intl.formatMessage({ id: 'common.no' })
                          : product.domestic
              },
              { label: 'phytosanitaryProduct.conditioning', value: product.conditioning },
              { label: 'phytosanitaryProduct.storageSafety', value: product.storageSafety },
              { label: 'phytosanitaryProduct.handlingSafety', value: product.handlingSafety },
              { label: 'phytosanitaryProduct.disposalSafety', value: product.disposalSafety },
              { label: 'phytosanitaryProduct.inscriptionDate', value: product.inscriptionDate?.format('DD/MM/YYYY') },
              { label: 'phytosanitaryProduct.renewalDate', value: product.renewalDate?.format('DD/MM/YYYY') },
              { label: 'phytosanitaryProduct.modificationDate', value: product.modificationDate?.format('DD/MM/YYYY') },
              { label: 'phytosanitaryProduct.expiryDate', value: product.expiryDate?.format('DD/MM/YYYY') },
              { label: 'phytosanitaryProduct.saleLimitDate', value: product.saleLimitDate?.format('DD/MM/YYYY') }
          ].filter((item) => item.value)
        : [];

    return (
        <>
            <Tooltip title={<FormattedMessage id="phytosanitaryProduct.info.tooltip" />}>
                <Button type="text" size="small" icon={<InfoCircleOutlined />} onClick={showModal} />
            </Tooltip>
            {open && (
                <Modal open={open} onCancel={() => setOpen(false)} title={product?.name} footer={null} centered width={960}>
                    {loading && (
                        <div className={styles.loading}>
                            <Spin />
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <Descriptions column={1} size="small" bordered className={styles.productInfo}>
                            {items.map((item) => (
                                <Descriptions.Item key={item.label} label={<FormattedMessage id={item.label} />}>
                                    {item.value}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    )}
                    {!loading && items.length === 0 && (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<FormattedMessage id="phytosanitaryProduct.notFound" />} />
                    )}
                </Modal>
            )}
        </>
    );
};

export default PhytosanitaryProductComponent;

interface PhytosanitaryProductProps {
    registerNumber?: string;
}
